import { useCallback, useMemo, useState } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, IconButton, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';
import { EyeIcon, Trash2Icon } from 'lucide-react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type GetCmaReportsListQuery,
  Order_By,
} from '@graphql/__generated__/graphql';
import { CmaGenerationProgress } from '@modules/_shared/components/CmaGenerationProgress';
import { PropertyCell } from '@modules/_shared/components/data-grid/PropertyCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { UserInfo } from '@modules/_shared/components/data-grid/UserInfo';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { TemplateViewerDialog } from '@modules/_shared/components/TemplateViewerDialog';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { TopbarTitle } from '@modules/_shared/components/topbar/Topbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getGlobalTemplateId } from '@modules/_shared/utils/document-url';
import { formatCmaReportPrice } from '@modules/_shared/utils/formatting';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { getAgentFilter } from '@modules/_shared/utils/quickFilters';
import CreateCMAReport from '@modules/create-cma-report/CreateCMAReport';
import {
  DELETE_CMA_REPORT,
  GET_CMA_REPORTS_COUNT,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

export const initialCMAReportsListQuery = (
  searchParams: URLSearchParams,
): QueryOptions => {
  const where = JSON.parse(searchParams.get('where') ?? '{}');
  const order_by = searchParams.get('order_by')
    ? JSON.parse(searchParams.get('order_by')!)
    : [{ created_at: Order_By.Desc }];

  const { limit, offset } = getPaginationVariables(searchParams);

  return {
    query: GET_CMA_REPORTS_LIST,
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
  };
};

export const GET_CMA_REPORTS_LIST = gql(/* GraphQL */ `
  query GetCMAReportsList(
    $where: cma_reports_bool_exp!
    $order_by: [cma_reports_order_by!]
    $limit: Int!
    $offset: Int!
  ) {
    cma_reports(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      language
      suggested_market_value
      is_price_range
      price_range_min
      price_range_max
      language
      broker {
        ...UserInfo_user
      }
      contact {
        ...UserInfo_user
      }
      cover_image {
        url
      }
      doc_template {
        id
        name
      }
      updated_at
      created_at

      property {
        ...PropertyCell_property
      }
    }
  }
`);

const Actions = ({ node }: { node: CMAReport }) => {
  const { t } = useLocale();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const [deleteCmaReport, { loading: deleting }] = useMutation(
    DELETE_CMA_REPORT,
    {
      variables: { id: node.id },
      refetchQueries: [GET_CMA_REPORTS_LIST, GET_CMA_REPORTS_COUNT],
    },
  );

  return (
    <>
      <SlideDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title={t('Delete CMA Report')}
        onSave={() => {
          deleteCmaReport({
            update: cache => {
              cache.evict({ id: `cma_reports:${node.id}` });
              setOpenDeleteDialog(false);
            },
          });
        }}
        saveText={t('Delete')}
        saveColor="error"
        saveIcon={<Trash2Icon />}
        cancelText={t('Cancel')}
        loading={deleting}
        maxWidth="xs"
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Typography variant="body1">
            {t('Are you sure you want to delete this CMA report?')}
          </Typography>
        </Box>
      </SlideDialog>
      <IconButton title={t('View')} onClick={() => setTemplateOpen(true)}>
        <EyeIcon size={20} />
      </IconButton>
      <IconButton
        title={t('Delete')}
        color="error"
        onClick={() => setOpenDeleteDialog(true)}
      >
        <Trash2Icon size={20} />
      </IconButton>
      <TemplateViewerDialog
        templateId={getGlobalTemplateId(node.doc_template?.id ?? '')}
        documentId={toGlobalId('CmaReport', node.id)}
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
      />
    </>
  );
};

type CMAReport = GetCmaReportsListQuery['cma_reports'][number];

const CMAReports = () => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { variables } = useMemo(
    () => initialCMAReportsListQuery(searchParams),
    [searchParams],
  );
  const { where, order_by, limit, offset } = variables ?? {};

  const { data, loading } = useQuery(GET_CMA_REPORTS_LIST, {
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
  });
  const cmaReports = data?.cma_reports ?? [];

  const { data: totalCountData } = useQuery(GET_CMA_REPORTS_COUNT, {
    variables: { where },
  });

  const totalCount =
    totalCountData?.cma_reports_aggregate?.aggregate?.count ?? 0;

  const cmaReportsLink = (id: string) => ({
    pathname: `${id}/page-cover`,
    search: `${searchParams.toString()}`,
  });

  const columns: GridColDef<CMAReport>[] = [
    {
      field: 'property.route,property.street_number',
      headerName: t('Property'),
      width: 250,
      renderCell: ({ row }) => {
        return (
          row.property && (
            <PropertyCell
              image_url={row.cover_image?.url}
              property={row.property}
            />
          )
        );
      },
    },
    {
      field: 'contact.first_name,contact.last_name',
      headerName: t('Contact'),
      width: 200,
      renderCell: ({ row }) =>
        row.contact == null ? null : <UserInfo user={row.contact} />,
    },
    {
      field: 'broker.first_name,broker.last_name',
      headerName: t('broker'),
      width: 250,
      renderCell: ({ row }) =>
        row.broker != null && <UserInfo user={row.broker} />,
    },
    {
      field: 'suggested_market_value',
      headerName: t('Suggested market value'),
      sortable: false,
      width: 200,
      align: 'right',
      renderCell: ({ row }) => {
        const currency = getCurrencyByCountryCode(
          row.property?.country_code ?? 'CH',
        );
        return <div>{formatCmaReportPrice(row, currency, locale)}</div>;
      },
    },
    {
      field: 'updated_at',
      headerName: t('Last edited'),
      width: 150,
      renderCell: ({ row }) => <TimeAgo dateString={row.updated_at} />,
    },
    {
      field: 'created_at',
      headerName: t('Created'),
      width: 150,
      renderCell: ({ row }) => <TimeAgo dateString={row.created_at} />,
    },
    {
      field: 'actions',
      flex: 1,
      sortable: false,
      type: 'actions',
      align: 'right',
      renderCell: ({ row }) => <Actions node={row} />,
    },
  ];

  const handleCreateCMA = useCallback(
    (newCMAReportId: string) => {
      searchParams.set('language', me?.language);
      setSearchParams(searchParams);
      navigate({
        pathname: `./${newCMAReportId}/page-cover`,
        search: searchParams.toString(),
      });
    },
    [me?.language, navigate, searchParams, setSearchParams],
  );

  const isAutoGenerating =
    location.pathname.includes('/new') && searchParams.get('id') != null;

  return (
    <>
      <TopbarTitle>{t('CMA reports')}</TopbarTitle>
      <ListToolbar
        tableName="cma_reports"
        quickFilters={[
          {
            label: t('Places'),
            path: ['cma_reports_bool_exp', 'property', 'locality_place'],
            includeParentsPlaces: true,
            expandSelectionAncestors: true,
          },
          {
            label: t('Contact'),
            path: ['cma_reports_bool_exp', 'contact'],
          },
          {
            label: t('Agent'),
            path: ['cma_reports_bool_exp', 'broker'],
            filter: getAgentFilter(me),
          },
          {
            label: t('Completed'),
            path: ['cma_reports_bool_exp', 'is_completed'],
          },
          {
            label: t('Updated'),
            path: ['cma_reports_bool_exp', 'updated_at'],
          },
          {
            label: t('Created'),
            path: ['cma_reports_bool_exp', 'created_at'],
          },
        ]}
        newLink={{ pathname: 'new', search: searchParams.toString() }}
      />
      {isAutoGenerating ? (
        <CmaGenerationProgress isLoading variant="fullscreen" />
      ) : (
        <RaDataGrid
          columns={columns}
          rows={cmaReports}
          loading={loading}
          rowCount={totalCount}
          onRowClick={({ id }) => navigate(cmaReportsLink(id as string))}
        />
      )}

      <Routes>
        <Route
          path="new/*"
          element={
            <CreateCMAReport
              onClose={() => {
                navigate({
                  pathname: '.',
                  search: searchParams.toString(),
                });
              }}
              onCreated={handleCreateCMA}
            />
          }
        />
      </Routes>
    </>
  );
};

export default CMAReports;
